import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  withStyles,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import { connect } from 'react-redux';
import { appService } from '../../App/app.service';
import AddHeaderDescriptionWithHelperText from '../../components/HelperTextDescription';
import RSMPerformanceTable from './RSMPerformanceTable';

const styles = (theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: '#fafafa',
    minHeight: '100vh',
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  title: {
    fontWeight: 600,
    color: '#333',
  },
  subtitle: {
    color: '#666',
    fontSize: '0.875rem',
  },
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  updateFrequency: {
    backgroundColor: '#00bcd4',
    color: '#fff',
    padding: '4px 12px',
    borderRadius: 4,
    fontSize: '0.75rem',
    fontWeight: 500,
    marginLeft: theme.spacing(2),
  },
  filterWrapper: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  filterContainer: {
    padding: theme.spacing(2),
    backgroundColor: '#fff',
    borderRadius: 8,
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    display: 'inline-flex',
    width: 'auto',
  },
  formControl: {
    minWidth: 200,
  },
  goButton: {
    borderRadius: 4,
    textTransform: 'none',
    fontWeight: 600,
    padding: '8px 24px',
  },
  filterLabel: {
    color: '#000',
  },
});

const RegionalRSMPerformanceSummary = (props) => {
  const { classes, rsmFirstLevelTeams, rsmDefaultTeam, rsmPerformanceData, helperTextCharts } = props;

  // Local state for pending team selection (before clicking Go)
  const [pendingTeam, setPendingTeam] = useState('All');
  // Applied team filter (after clicking Go)
  const [appliedTeam, setAppliedTeam] = useState('All');
  // Track if initial data has been loaded
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  // Reset to 'All' when component mounts
  useEffect(() => {
    setPendingTeam('All');
    setAppliedTeam('All');
    setInitialLoadDone(false);
  }, []);

  // Fetch first-level teams on mount
  useEffect(() => {
    props.getFirstLevelTeams();
  }, []);

  // Set default team when teams are loaded (uses manager's team if applicable)
  useEffect(() => {
    if (!initialLoadDone && rsmFirstLevelTeams && rsmFirstLevelTeams.length > 0) {
      const defaultTeam = rsmDefaultTeam || 'All';
      setPendingTeam(defaultTeam);
      setAppliedTeam(defaultTeam);
      const filters = defaultTeam !== 'All' ? [{ name: 'team', value: defaultTeam }] : [];
      props.getRSMPerformanceData(filters);
      setInitialLoadDone(true);
    }
  }, [rsmFirstLevelTeams, rsmDefaultTeam, initialLoadDone]);

  // Handle team filter change (just updates pending, doesn't fetch)
  const handleTeamChange = (event) => {
    setPendingTeam(event.target.value);
  };

  // Handle Go button click - apply the filter and fetch data
  const handleGo = () => {
    setAppliedTeam(pendingTeam);
    const filters = pendingTeam !== 'All' ? [{ name: 'team', value: pendingTeam }] : [];
    props.getRSMPerformanceData(filters);
  };

  const isLoading = !rsmPerformanceData || rsmPerformanceData === "";

  return (
    <div className={classes.root}>
      <AddHeaderDescriptionWithHelperText
        header={`Regional RSM Performance Summary`}
        description={`Regional RSM Performance Summary.`}
        helperTextCharts={helperTextCharts}
      />

      {/* Team Filter */}
      <Box className={classes.filterWrapper}>
        <Paper className={classes.filterContainer} elevation={0}>
          <FormControl className={classes.formControl}>
            <InputLabel
              className={classes.filterLabel}
              id="rsm-team-filter-label"
              style={{
                color: pendingTeam !== '' && pendingTeam !== 'All' ? '#4472c4' : '#000',
              }}
            >
              Team:
            </InputLabel>
            <Select
              labelId="rsm-team-filter-label"
              id="rsm-team-filter"
              value={pendingTeam}
              onChange={handleTeamChange}
              label="Team:"
            >
              <MenuItem value="All">
                All
              </MenuItem>
              {rsmFirstLevelTeams && rsmFirstLevelTeams.map((team) => (
                <MenuItem key={team} value={team}>
                  {team}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>
        <Button
          color="primary"
          variant="contained"
          onClick={handleGo}
          className={classes.goButton}
        >
          GO
        </Button>
      </Box>

      {/* RSM Performance Table */}
      <Paper className={classes.paper}>
        <div className={classes.header}>
          <Box display="flex" flexDirection='column' alignItems="center">
            <Typography variant="h5" className={classes.title}>
              {rsmPerformanceData?.title || 'RSM Performance Summary'}
            </Typography>
            <span className={classes.updateFrequency}>
              {'Live Data'}
            </span>
          </Box>
        </div>

        {isLoading ? (
          <Skeleton variant="rect" animation="wave" height={400} width="100%" />
        ) : rsmPerformanceData?.tableData?.length > 0 ? (
          <RSMPerformanceTable
            data={rsmPerformanceData.tableData}
            fiscalYear={rsmPerformanceData.fiscalYear}
            quarters={rsmPerformanceData.quarters}
            stages={rsmPerformanceData.stages}
          />
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <Typography variant="body1" color="textSecondary">
              No data available
            </Typography>
          </Box>
        )}
      </Paper>
    </div>
  );
};

const mapStateToProps = (state) => {
  const { user, rsmFirstLevelTeams, rsmDefaultTeam, rsmPerformanceData } = state.app;

  return {
    company: user.company,
    Value_Label: user.Value_Label,
    rsmFirstLevelTeams: rsmFirstLevelTeams || [],
    rsmDefaultTeam: rsmDefaultTeam || 'All',
    rsmPerformanceData: rsmPerformanceData || null,
    helperTextCharts: user.elements.flatMap((item) => item.charts || []),
  };
};

const mapDispatchToProps = (dispatch) => ({
  getFirstLevelTeams: () => {
    dispatch({ type: "get_rsm_first_level_teams_request" });
    appService.getPipelineReviewFirstLevelTeams().then(
      (json) => {
        dispatch({ type: "get_rsm_first_level_teams_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_rsm_first_level_teams_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_rsm_first_level_teams_failure", error });
      }
    );
  },
  getRSMPerformanceData: (filters = []) => {
    dispatch({ type: "get_rsm_performance_data_request" });
    appService.getRSMPerformanceSummary(filters).then(
      (json) => {
        dispatch({ type: "get_rsm_performance_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_rsm_performance_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_rsm_performance_data_failure", error });
      }
    );
  },
});

const connectedRegionalRSMPerformanceSummary = connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(RegionalRSMPerformanceSummary));

export default connectedRegionalRSMPerformanceSummary;
